import { Divider, makeStyles, Tooltip } from '@material-ui/core';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { theme } from '../../theme/theme';
import { Button, IconButton, ProgressBar } from '../global';
import { motions } from '../global/';

const useProgressTrackerClasses = makeStyles({
    root: {
        display: 'flex',
        borderRadius: 5,
        boxShadow: '0 3px 20px -5px rgba(16, 20, 53, 0.5)',
        background: theme.colors.darkBlue[1],
        color: theme.colors.white,
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: 20,
        '& > span:first-of-type': {
            ...theme.typography.content.label.strong,
        },
        '& > span:last-of-type': {
            ...theme.typography.content.caption.paragraph,
            marginBottom: 10,
        },
        '& > :last-child': {
            maxWidth: 220,
        },
    },
    icon: {
        alignSelf: 'center',
        margin: '0 5px',
        color: theme.colors.neutral[3],
    },
    button: {
        display: 'flex',
        '& > hr': {
            background: theme.colors.green[4],
        },
        '& > button': {
            padding: 25,
            color: `${theme.colors.white} !important`,
        },
    },
    tooltip: {
        display: 'flex',
        justifyContent: 'center',
        minWidth: 130,
        padding: 8,
        borderRadius: 4,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        background: theme.colors.neutral[4],
        color: theme.colors.darkBlue[2],
        ...theme.typography.content.label.medium,
        '& .MuiTooltip-arrow': {
            color: theme.colors.neutral[4],
        },
    },
});

/**
 * Bar to track progress and interact section workflows.
 *
 * @param props.title progress tracker title
 * @param props.subtitle progress tracker subtitle
 * @param props.number percentage completed (0 to 100)
 * @param props.actions list of actions containing a title, icon and click callback
 * @param props.button side button text and click callback
 */
export const ProgressTracker = (props: {
    title: string;
    subtitle: string;
    completed: number;
    actions: { hidden?: boolean; title: string; icon: React.ReactNode; onClick?: () => void }[];
    button?: { hidden?: boolean; text: React.ReactNode; icon?: React.ReactNode; onClick?: () => void };
}) => {
    const classes = useProgressTrackerClasses();

    return (
        <div className={classes.root}>
            <div className={classes.labels}>
                <span>{props.title}</span>
                <span>{props.subtitle}</span>
                <ProgressBar variant='determinate' value={props.completed} />
            </div>
            <AnimatePresence>
                {props.actions
                    .filter(action => !action.hidden)
                    .map(action => (
                        <Tooltip
                            classes={{ tooltip: classes.tooltip }}
                            title={action.title.charAt(0).toUpperCase() + action.title.slice(1)}
                            placement='top'
                            arrow
                        >
                            <motion.div key={action.title} className={classes.icon} {...motions.presence({})}>
                                <IconButton onClick={action.onClick}>{action.icon}</IconButton>
                            </motion.div>
                        </Tooltip>
                    ))}
            </AnimatePresence>
            <AnimatePresence>
                {props.button && !props.button.hidden && (
                    <motion.div className={classes.button} {...motions.presence({ slide: 10 })}>
                        <Divider orientation='vertical' />
                        <Button endIcon={props.button.icon} onClick={props.button.onClick}>
                            {props.button.text}
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
